// The one today-vs-dated rule: "7:00 PM EDT" when an instant falls today in a
// zone, "Oct 2, 7:00 PM EDT" when it does not (BUG-1586, lifted by BUG-1712).
import type { TimeInput } from './types';
import type { FormatEventTimeOptions } from './format-event-time';
import { isSameDayInZone } from './is-same-day-in-zone';

// Frozen singletons so a caller rendering every second hands `<EventTime>` a
// stable options object rather than a fresh one per tick.
const TIME_ONLY: FormatEventTimeOptions = Object.freeze({ timeOnly: true });
const DATED: FormatEventTimeOptions = Object.freeze({ omitYear: true });

/**
 * The {@link formatEventTime} options for an instant shown relative to "now":
 * time-only when it falls on the same calendar day as `now` in `zone`, the
 * dated year-less form otherwise.
 *
 * One rule, so every surface that switches on "is this today" — the clerk
 * console's broadcast send-stamps (BUG-1586) and its lot-header close time
 * (BUG-1712) — decides it identically (ADR 044 — Must Consolidate). The day is
 * read in `zone`, never on the runtime's clock (ADR 024 — Time Model § 3b).
 *
 * Returns **null** when the question cannot be answered — no zone, or an
 * unparseable instant — rather than guessing a day. Callers choose their own
 * honest fallback: an em dash for an audit stamp, the dated form for a close
 * time (longer, but never reads as "tonight").
 *
 * @param input.instant - The instant being displayed.
 * @param input.now - The reference "now" (server-anchored where one exists).
 * @param input.zone - IANA zone the calendar day is read in (the auction's).
 * @returns `{ timeOnly: true }`, `{ omitYear: true }`, or `null`.
 * @sideEffect none — pure.
 */
export function todayOrDatedFormat({
    instant,
    now,
    zone,
}: {
    instant: TimeInput;
    now: TimeInput;
    zone: string;
}): FormatEventTimeOptions | null {
    const sameDay = isSameDayInZone({ a: instant, b: now, zone });
    if (sameDay == null) return null;
    return sameDay ? TIME_ONLY : DATED;
}
